import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { CairnMark } from './components/icons';
import { AppShell } from './components/layout';
import { Button, ToastProvider } from './components/ui';
import { I18nProvider } from './i18n';
import type { Locale } from './i18n/types';
import { api, type Theme } from './lib/api';
import { LedgerProvider, readStoredLedger } from './lib/ledger-context';
import { keys, useSession } from './lib/queries';
import { AcceptInviteScreen, ResetPasswordScreen, SetupScreen, SignInScreen } from './screens/Auth';
import { AddReceiptScreen } from './screens/AddReceipt';
import { DashboardScreen } from './screens/Dashboard';
import { InsightsScreen } from './screens/Insights';
import { ReceiptDetailScreen } from './screens/ReceiptDetail';
import { ReceiptsScreen } from './screens/Receipts';
import { SettingsScreen } from './screens/Settings';

const VERSION = import.meta.env.VITE_CAIRN_VERSION ?? 'dev';

/**
 * Theme and language are stored on the server so they follow a person between
 * devices, and mirrored into localStorage so the very first paint is already
 * right — before any request has come back.
 */
function useTheme(): [Theme, (theme: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('cairn.theme') as Theme | null) ?? 'system',
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    localStorage.setItem('cairn.theme', theme);
  }, [theme]);

  return [theme, setTheme];
}

function useLocaleState(fallback: Locale): [Locale, (locale: Locale) => void] {
  const [locale, setLocale] = useState<Locale>(() => {
    const saved = localStorage.getItem('cairn.locale');
    if (saved === 'en' || saved === 'zh-CN') return saved;
    // Before anyone has chosen, follow the browser: a Chinese-speaking visitor
    // should not have to find a language switch first.
    return navigator.language.toLowerCase().startsWith('zh') ? 'zh-CN' : fallback;
  });

  const update = useCallback((next: Locale) => {
    setLocale(next);
    localStorage.setItem('cairn.locale', next);
  }, []);

  return [locale, update];
}

function FullScreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--ground)]">
      <CairnMark size={40} className="animate-pulse text-[var(--ink-muted)]" />
    </div>
  );
}

function ErrorScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-[var(--ground)] px-6 text-center">
      <CairnMark size={36} className="text-[var(--ink-muted)]" />
      <p className="text-[15px] text-[var(--ink-secondary)]">Cairn could not reach its server.</p>
      <Button variant="secondary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

export function App() {
  const queryClient = useQueryClient();
  const session = useSession();
  const [theme, setTheme] = useTheme();
  const location = useLocation();

  const workspace = session.data?.workspace;
  const [locale, setLocale] = useLocaleState(workspace?.defaultLocale ?? 'en');
  const [ledgerId, setLedgerId] = useState<string | null>(() => readStoredLedger());

  // Once signed in, the account's own language preference wins over the guess
  // made from the browser.
  const user = session.data?.user ?? null;
  useEffect(() => {
    if (user && (user.locale === 'en' || user.locale === 'zh-CN')) {
      setLocale(user.locale);
    }
    if (user) setTheme(user.theme);
    // Only when the signed-in identity changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Called after signing in, completing setup, or accepting an invitation —
   * every point where *who you are* changes. Everything cached belonged to the
   * previous identity (often "nobody"), so it all goes rather than just the
   * session.
   */
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries();
    void queryClient.invalidateQueries({ queryKey: keys.session });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    queryClient.clear();
    refresh();
  }, [queryClient, refresh]);

  const content = useMemo(() => {
    if (session.isLoading) return <FullScreenLoader />;
    if (session.isError) return <ErrorScreen onRetry={refresh} />;

    const data = session.data;
    if (!data) return <FullScreenLoader />;

    // Invitation and reset links have to work for signed-out visitors, and must
    // not be swallowed by the setup or sign-in redirect below.
    const isPublicToken =
      location.pathname.startsWith('/invite/') || location.pathname.startsWith('/reset/');

    if (isPublicToken) {
      return (
        <Routes>
          <Route
            path="/invite/:token"
            element={
              <AcceptInviteScreen locale={locale} onLocaleChange={setLocale} onComplete={refresh} />
            }
          />
          <Route path="/reset/:token" element={<ResetPasswordScreen />} />
        </Routes>
      );
    }

    if (data.setupRequired) {
      return <SetupScreen locale={locale} onLocaleChange={setLocale} onComplete={refresh} />;
    }

    if (!data.user) return <SignInScreen onSignedIn={refresh} />;

    return (
      <AppShell workspaceName={data.workspace.name}>
        <Routes>
          <Route
            path="/"
            element={
              <DashboardScreen user={data.user} paperlessReady={data.capabilities.paperless} />
            }
          />
          <Route path="/receipts" element={<ReceiptsScreen />} />
          <Route
            path="/receipts/:id"
            element={<ReceiptDetailScreen paperlessReady={data.capabilities.paperless} />}
          />
          <Route
            path="/add"
            element={<AddReceiptScreen paperlessReady={data.capabilities.paperless} />}
          />
          <Route path="/insights" element={<InsightsScreen />} />
          <Route
            path="/settings"
            element={
              <SettingsScreen
                user={data.user}
                theme={theme}
                onThemeChange={setTheme}
                onSignOut={() => void signOut()}
                version={VERSION}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    );
  }, [session, locale, setLocale, refresh, location.pathname, theme, setTheme, signOut]);

  return (
    <I18nProvider
      locale={locale}
      currency={workspace?.currency ?? 'USD'}
      onLocaleChange={setLocale}
    >
      <LedgerProvider
        selected={ledgerId}
        onSelect={setLedgerId}
        signedIn={Boolean(session.data?.user)}
      >
        <ToastProvider>{content}</ToastProvider>
      </LedgerProvider>
    </I18nProvider>
  );
}
